import i18n from '../../../../../i18n';
import { DATA_NOT_AVAILABLE_VALUE } from '../../../../common/api/api-interfaces';
import {
  MetricCategoryId,
  MetricId,
} from '../../../../common/graphql/generated/graphql-sdk';
import { MetricStore } from '../../../../common/metrics/metric-store';
import { enumValues } from '../../../../common/utilFunctions/pure-utils';
import { percentage } from '../../../../common/utilFunctions/utils';
import {
  CONFIDENTIAL_DISPLAY_KEY,
  CONFIDENTIAL_VALUE,
} from '../../../../common/v2/constants';
import { formatPercentageNumber } from '../../../../common/v2/utils-display';
import { CellValGetter } from '../BalanceSheetTable';
import {
  DELTA_PERCENTAGE,
  PbsServiceResponseData,
  TimeHeaderData,
} from '../types';

export const timeHeaderCellValGetter: CellValGetter = (
  dataToUse,
  timeHeaderVal,
  selectedMeasure
) => {
  // Very weird code here, ideally should have stored as
  // object so I don't have to do this find thing
  const cellData = dataToUse.timeHeadersData.find(
    (d: TimeHeaderData) => d.label === timeHeaderVal
  )!.data;
  const cellVal = cellData?.[selectedMeasure];
  return cellVal ?? null;
};

export const fixedHeaderCellValGetter: CellValGetter = (
  dataToUse,
  fixedHeaderVal,
  selectedMeasure
) => {
  const cellData = dataToUse.fixedHeadersData[fixedHeaderVal];
  const cellVal = cellData?.[selectedMeasure];
  return cellVal ?? null;
};

export const benchmarkHeaderCellValGetter: CellValGetter = (
  dataToUse,
  benchmarkHeaderVal,
  selectedMeasure
) => {
  const cellData = dataToUse.benchmarkHeadersData![benchmarkHeaderVal];
  const cellVal = cellData?.[selectedMeasure];
  return cellVal ?? null;
};

export const getCellValForSegmentMeasure = (
  cellValGetter: (dataToUse: PbsServiceResponseData) => number | null,
  data: PbsServiceResponseData,
  totalData: PbsServiceResponseData,
  isPercentage: boolean
) => {
  const segmentCellVal = cellValGetter(data);
  const totalCellVal = cellValGetter(totalData);
  if (segmentCellVal === null || totalCellVal === null) {
    return null;
  }
  const cellVal = isPercentage
    ? percentage(segmentCellVal, totalCellVal)
    : segmentCellVal;
  return cellVal;
};

export const getFormattedValForHeaderGetter =
  (cellValGetter: CellValGetter) =>
  (
    headerVal: string,
    selectedMeasure: MetricId,
    data: PbsServiceResponseData,
    totalData: PbsServiceResponseData,
    isPercentage: boolean,
    metricStore: MetricStore
  ) => {
    const cellVal = getCellValForSegmentMeasure(
      (dataToUse) => cellValGetter(dataToUse, headerVal, selectedMeasure),
      data,
      totalData,
      isPercentage
    );
    if (cellVal === null) {
      return DATA_NOT_AVAILABLE_VALUE;
    }
    if (typeof cellVal === 'string') {
      if (cellVal === CONFIDENTIAL_VALUE) {
        return i18n.t(CONFIDENTIAL_DISPLAY_KEY);
      }
      return cellVal;
    }
    const formattedVal =
      isPercentage || headerVal === DELTA_PERCENTAGE
        ? formatPercentageNumber(cellVal)
        : metricStore.formatMetricValue(cellVal, selectedMeasure);
    return formattedVal;
  };

export const getFormattedValForTimeHeader = getFormattedValForHeaderGetter(
  timeHeaderCellValGetter
);
export const getFormattedValForFixedHeader = getFormattedValForHeaderGetter(
  fixedHeaderCellValGetter
);
export const getFormattedValForBenchmarkHeader = getFormattedValForHeaderGetter(
  benchmarkHeaderCellValGetter
);

/**
 * True when a value produced by {@link getFormattedValForHeaderGetter} is the
 * "Confidential" placeholder rather than real data. Cells in this state must never be
 * wired up to open the Data Explorer, since drilling down would let a user recover the
 * individual employees a metric suppressed for privacy.
 */
export const isConfidentialFormattedVal = (
  formattedVal: string | number
): boolean => formattedVal === i18n.t(CONFIDENTIAL_DISPLAY_KEY);

export const pbsCategorySortOrder: MetricCategoryId[] =
  enumValues(MetricCategoryId);
